import { Button } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { isSafari as getIsSafari } from 'src/lib/getBrowser';

const isSafari = getIsSafari();

/**
 * Warning: Do not use this component in a loop that might be larger than 100 items!
 * Since the component has a useEffect that reads from contentRef.current?.scrollHeight, each component mount will trigger a browser 🟪 Layout 🚨🚨
 *
 * Safari currently does not render line-clamp correctly if element with clamp doesn't directly wrap text
 * https://github.com/tailwindlabs/tailwindcss/issues/10994#issuecomment-1506193656
 */
export const CollapsibleText = ({
  maxLines,
  children,
  className,
  expandTextElement,
}: {
  maxLines: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
  children?: React.ReactNode;
  expandTextElement?: (props: {
    isCollapsed: boolean;
    onClick: () => void;
  }) => React.ReactNode;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isClamped, setIsClamped] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const canClamp = !isSafari || typeof children === 'string';

  useEffect(() => {
    if (!canClamp) return;
    const handleResize = () => {
      if (contentRef && contentRef.current && isCollapsed) {
        setIsClamped(
          contentRef.current?.scrollHeight > contentRef.current?.clientHeight,
        );
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed, canClamp]);

  return (
    <div>
      <div
        className={classnames(
          isCollapsed &&
            canClamp && {
              'line-clamp-1': maxLines === 1,
              'line-clamp-2': maxLines === 2,
              'line-clamp-3': maxLines === 3,
              'line-clamp-4': maxLines === 4,
              'line-clamp-5': maxLines === 5,
              'line-clamp-6': maxLines === 6,
            },
          className,
        )}
        ref={contentRef}
      >
        {children}
      </div>
      {isClamped &&
        (expandTextElement ? (
          expandTextElement({
            onClick: () => setIsCollapsed((value) => !value),
            isCollapsed,
          })
        ) : (
          <Button
            size="sm"
            variant="secondary"
            className="mt-1"
            onClick={() => setIsCollapsed((value) => !value)}
          >
            {isCollapsed ? 'Show more' : 'Show less'}
          </Button>
        ))}
    </div>
  );
};
